/**
 * Policy Analytics Hook
 *
 * FERP CRITICAL: Analytics for policy evaluation and usage tracking
 *
 * FERP Principles:
 * - Repository pattern (uses PolicyRepository for data access)
 * - Backend ID generation (Snowflake IDs only)
 * - Data-driven (all analytics from backend)
 * - Life-critical (complete error handling)
 * - Domain-agnostic (works for all policy types)
 *
 * Features:
 * - Policy evaluation statistics
 * - Usage trending over time
 * - Effectiveness scoring
 * - Top policies by evaluation count
 * - Performance metrics (evaluation time)
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';
import { useApolloClient } from '@apollo/client/react';
import { notifications } from '@mantine/notifications';
import {
  GetAnalyticsSummaryDocument,
  type GetAnalyticsSummaryQuery,
} from '@ferp/core/generated/graphql';

// ============================================================================
// Types
// ============================================================================

export interface PolicyAnalytics {
  policy_id: string;
  policy_name: string;
  evaluation_count: number;
  matched_count: number;
  blocked_count: number;
  approved_count: number;
  avg_evaluation_time_ms: number;
  effectiveness_score: number; // 0-100, based on matched/total ratio
  last_evaluated: string;
  trend: 'up' | 'down' | 'stable';
}

export interface PolicyUsageTrend {
  timestamp: string;
  evaluation_count: number;
  matched_count: number;
  avg_evaluation_time_ms: number;
}

export interface PolicyEvaluationHistory {
  evaluation_id: string;
  policy_id: string;
  timestamp: string;
  matched: boolean;
  evaluation_time_ms: number;
  triggered_actions: string[];
  input_data_summary: Record<string, any>;
}

export interface PolicyAnalyticsSummary {
  total_policies: number;
  active_policies: number;
  total_evaluations: number;
  avg_effectiveness: number;
  most_used_policies: PolicyAnalytics[];
  least_effective_policies: PolicyAnalytics[];
  performance_by_type: Record<string, number>; // policy_type -> avg_evaluation_time_ms
}

// ============================================================================
// GraphQL Documents
// ============================================================================
// Using generated documents from @ferp/core/generated/graphql
// - GetAnalyticsSummaryDocument: Tenant analytics summary
//
// Note: Other queries (GetPolicyAnalytics, GetPolicyUsageTrend, GetPolicyEvaluationHistory)
// need to be added to frontend/apps/portal/src/graphql/queries.graphql and regenerated.
// For now, these features return empty/null until the queries are added to the data layer.

// ============================================================================
// Hook Implementation
// ============================================================================

export function usePolicyAnalytics() {
  const client = useApolloClient();
  const { tenantId } = useCurrentTenant();

  const [analytics, setAnalytics] = useState<PolicyAnalytics | null>(null);
  const [usageTrend, setUsageTrend] = useState<PolicyUsageTrend[]>([]);
  const [evaluationHistory, setEvaluationHistory] = useState<PolicyEvaluationHistory[]>([]);
  const [summary, setSummary] = useState<PolicyAnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);

  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ============================================================================
  // Load Policy Analytics
  // ============================================================================

  const loadPolicyAnalytics = useCallback(
    async (_policyId: string, _timeRange: string = '7d'): Promise<PolicyAnalytics | null> => {
      // TODO: Add GetPolicyAnalytics query to data layer (queries.graphql)
      // For now, return null - this feature needs query migration
      console.warn('loadPolicyAnalytics: Query not yet in data layer');
      return null;
    },
    []
  );

  // ============================================================================
  // Load Usage Trend
  // ============================================================================

  const loadUsageTrend = useCallback(
    async (_policyId: string, _timeRange: string = '7d'): Promise<PolicyUsageTrend[]> => {
      // TODO: Add GetPolicyUsageTrend query to data layer (queries.graphql)
      console.warn('loadUsageTrend: Query not yet in data layer');
      return [];
    },
    []
  );

  // ============================================================================
  // Load Evaluation History
  // ============================================================================

  const loadEvaluationHistory = useCallback(
    async (_policyId: string, _limit: number = 50): Promise<PolicyEvaluationHistory[]> => {
      // TODO: Add GetPolicyEvaluationHistory query to data layer (queries.graphql)
      console.warn('loadEvaluationHistory: Query not yet in data layer');
      return [];
    },
    []
  );

  // ============================================================================
  // Load Analytics Summary
  // ============================================================================

  const loadSummary = useCallback(
    async (_timeRange: string = '7d'): Promise<PolicyAnalyticsSummary | null> => {
      setLoading(true);
      try {
        const { data } = await client.query<GetAnalyticsSummaryQuery>({
          query: GetAnalyticsSummaryDocument,
          variables: { tenantId },
        });

        if (!data?.getAnalyticsSummary) {
          return null;
        }

        const summaryData = mapAnalyticsSummary(data.getAnalyticsSummary);
        setSummary(summaryData);
        return summaryData;
      } catch (error) {
        console.error('Failed to load analytics summary:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to load analytics summary',
          color: 'red',
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId]
  );

  // ============================================================================
  // Auto-Refresh Management
  // ============================================================================

  const startAutoRefresh = useCallback(
    (policyId: string, intervalMs: number = 30000) => {
      setAutoRefreshEnabled(true);
      autoRefreshTimerRef.current = setInterval(() => {
        loadPolicyAnalytics(policyId);
        loadUsageTrend(policyId);
      }, intervalMs);
    },
    [loadPolicyAnalytics, loadUsageTrend]
  );

  const stopAutoRefresh = useCallback(() => {
    setAutoRefreshEnabled(false);
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
    };
  }, []);

  // ============================================================================
  // Return Hook Interface
  // ============================================================================

  return {
    // State
    analytics,
    usageTrend,
    evaluationHistory,
    summary,
    loading,
    autoRefreshEnabled,

    // Actions
    loadPolicyAnalytics,
    loadUsageTrend,
    loadEvaluationHistory,
    loadSummary,
    startAutoRefresh,
    stopAutoRefresh,
  };
}

// ============================================================================
// Mapping Functions (GraphQL → Frontend Types)
// ============================================================================

function mapPolicyAnalytics(gqlData: any): PolicyAnalytics {
  return {
    policy_id: gqlData.policyId,
    policy_name: gqlData.policyName,
    evaluation_count: gqlData.evaluationCount,
    matched_count: gqlData.matchedCount,
    blocked_count: gqlData.blockedCount,
    approved_count: gqlData.approvedCount,
    avg_evaluation_time_ms: gqlData.avgEvaluationTimeMs,
    effectiveness_score: gqlData.effectivenessScore,
    last_evaluated: gqlData.lastEvaluated,
    trend: gqlData.trend,
  };
}

function mapUsageTrend(gqlData: any): PolicyUsageTrend {
  return {
    timestamp: gqlData.timestamp,
    evaluation_count: gqlData.evaluationCount,
    matched_count: gqlData.matchedCount,
    avg_evaluation_time_ms: gqlData.avgEvaluationTimeMs,
  };
}

function mapEvaluationHistory(gqlData: any): PolicyEvaluationHistory {
  return {
    evaluation_id: gqlData.evaluationId,
    policy_id: gqlData.policyId,
    timestamp: gqlData.timestamp,
    matched: gqlData.matched,
    evaluation_time_ms: gqlData.evaluationTimeMs,
    triggered_actions: gqlData.triggeredActions,
    input_data_summary: gqlData.inputDataSummary,
  };
}

function mapAnalyticsSummary(gqlData: any): PolicyAnalyticsSummary {
  // Backend provides different structure - adapt to frontend expectations
  return {
    total_policies: gqlData.totalPolicies ?? 0,
    active_policies: gqlData.totalPolicies ?? 0, // Backend doesn't distinguish active vs total
    total_evaluations: gqlData.totalEvaluations ?? 0,
    avg_effectiveness: gqlData.overallEffectiveness ?? 0,
    most_used_policies: [], // Backend doesn't provide ranked lists in summary
    least_effective_policies: [], // Use getTopPolicies query for rankings
    performance_by_type: {}, // Backend doesn't provide by-type breakdown in summary
  };
}
